import { useMemo, useState } from "react"
import { capitalize, difference } from "lodash"
import { LinkSimple } from "@phosphor-icons/react"

import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
} from "~/components/reusable/Table"
import { Input } from "~/components/reusable/Input"
import { AdjustmentResplitWarning } from "~/components/forecast/dialogs/AdjustmentResplitWarning"
import { type AggregatedAdjustment, convertAdjustmentValue } from "~/models/adjustment"
import { useAdjustments } from "~/hooks/useAdjustments"
import { useAddVariantsToAdjustment } from "~/hooks/useAdjustmentManager"
import { aggregateGlobal } from "~/utils/adjustmentsUtility"
import { api } from "~/utils/api"
import { formatDate, getStartOfCurrentWeekUTC, isAfter } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { Badge } from "~/components/reusable/Badge"
import { trackEvent } from "~/hooks/useTrackingService"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"

type Props = {
  variantIds: string[]
}

export function JoinExistingAdjustmentDialog({ variantIds }: Props) {
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [selectedAdjustment, setSelectedAdjustment] = useState<AggregatedAdjustment | null>(null)

  const { data: adjustments, isLoading } = useAdjustments()
  const { data: warehouses } = api.warehouse.get.useQuery()
  const addVariants = useAddVariantsToAdjustment()

  const activeAdjustments = useMemo(() => {
    if (!adjustments || !warehouses) return []
    const now = getStartOfCurrentWeekUTC()
    let result = aggregateGlobal(adjustments, warehouses)
      .filter((adj) => isAfter(adj.endDate, now))
      .filter((adj) => difference(variantIds, adj.variantIds).length > 0)

    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim()
      result = result.filter((adj) => adj.name.toLowerCase().includes(search))
    }

    return result
  }, [adjustments, warehouses, searchText, variantIds])

  const countToAdd = useMemo(
    () =>
      selectedAdjustment
        ? difference(variantIds, selectedAdjustment.variantIds).length
        : variantIds.length,
    [selectedAdjustment, variantIds],
  )

  const handleJoin = () => {
    if (!selectedAdjustment) return
    addVariants.mutate(
      {
        groupId: selectedAdjustment.groupId,
        value: selectedAdjustment.value,
        variantIds,
        warehouseId:
          selectedAdjustment.warehouseId === "Global" ? undefined : selectedAdjustment.warehouseId,
      },
      {
        onSuccess: () => {
          trackEvent("Adjustment Joined", { "Adjustment Name": selectedAdjustment.name })
          setOpen(false)
          setSelectedAdjustment(null)
        },
      },
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setSearchText("")
      setSelectedAdjustment(null)
    }
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger requires="edit" asChild>
        <Button variant="neutral" size="base">
          <LinkSimple size={14} />
          Add to existing adjustment
        </Button>
      </DialogTrigger>
      <DialogContentV2 className="max-w-[600px] max-h-[70vh]">
        <DialogHeaderV2>
          <DialogTitle>Add to existing adjustment</DialogTitle>
        </DialogHeaderV2>

        <DialogBodyV2 className="flex flex-col gap-3 overflow-y-auto">
          <p className="text-xs text-fg-subtle">
            Add {formatNumber(countToAdd, 0)} variant{countToAdd !== 1 ? "s" : ""} to an existing
            adjustment. They will inherit the adjustment&apos;s value and type.
          </p>

          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search adjustments by name..."
            containerClassName="w-full"
          />

          <div className="max-h-[300px] overflow-y-auto">
            {isLoading ? (
              <LoadingOverlay />
            ) : activeAdjustments.length === 0 ? (
              <div className="py-8 text-center text-sm text-fg-disabled">
                {searchText ? "No adjustments match your search" : "No active adjustments found"}
              </div>
            ) : (
              <SimpleTable>
                <SimpleTableHeader>
                  <SimpleTableRow className="text-left font-medium border-none text-fg-subtle">
                    <SimpleTableHead>Name</SimpleTableHead>
                    <SimpleTableHead>Theme</SimpleTableHead>
                    <SimpleTableHead>Value</SimpleTableHead>
                    <SimpleTableHead>Period</SimpleTableHead>
                    <SimpleTableHead>Variants</SimpleTableHead>
                  </SimpleTableRow>
                </SimpleTableHeader>
                <SimpleTableBody>
                  {activeAdjustments.map((adj) => {
                    const key = `${adj.name}-${adj.warehouseId}`
                    const isSelected = selectedAdjustment
                      ? `${selectedAdjustment.name}-${selectedAdjustment.warehouseId}` === key
                      : false

                    return (
                      <SimpleTableRow
                        key={key}
                        className={`cursor-pointer ${
                          isSelected ? "bg-background-highlight" : "hover:bg-subtle"
                        }`}
                        onClick={() => setSelectedAdjustment(adj)}
                      >
                        <SimpleTableCell className="font-medium text-xs">
                          {adj.name}
                        </SimpleTableCell>
                        <SimpleTableCell className="text-xs">
                          {capitalize(adj.theme)}
                        </SimpleTableCell>
                        <SimpleTableCell className="text-xs">
                          {convertAdjustmentValue(adj.value, adj.type)}
                        </SimpleTableCell>
                        <SimpleTableCell className="text-xs">
                          {formatDate(adj.startDate)} - {formatDate(adj.endDate)}
                        </SimpleTableCell>
                        <SimpleTableCell>
                          <Badge size="xs" variant="neutral">
                            {formatNumber(adj.variantIds.length, 0)}
                          </Badge>
                        </SimpleTableCell>
                      </SimpleTableRow>
                    )
                  })}
                </SimpleTableBody>
              </SimpleTable>
            )}
          </div>
        </DialogBodyV2>

        {selectedAdjustment && (
          <div className="px-5 pb-4">
            <AdjustmentResplitWarning adjustment={selectedAdjustment} addingCount={countToAdd} />
          </div>
        )}

        <DialogFooterV2 className="flex justify-between">
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
          <Button
            requires="edit"
            onClick={handleJoin}
            disabled={!selectedAdjustment || countToAdd === 0}
            loading={addVariants.isPending}
          >
            Add {formatNumber(countToAdd, 0)} variant{countToAdd !== 1 ? "s" : ""} to adjustment
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
